import { RouteEnum } from '@/constants/enums/RouteEnum';
import appSchema from '@/middleware/appSchema';
import auth from '@/middleware/auth';
import lineBinding from '@/middleware/lineBinding';
import paid from '@/middleware/paid';
import webview from '@/middleware/webview';
import type { RouteMenuItem } from '@/models/interface/routeMeta';
import { base64UrlToNumber } from '@/utils/base64UrlFormat';
import { RouterView, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router';

/**
 * 付費會員可用功能路由
 */
export const paidFeatureRoutes: RouteRecordRaw[] = [
  {
    /**
     * Today
     */
    path: '/today',
    name: RouteEnum.Today,
    component: () => import('@/views/TodayView.vue'),
    meta: {
      menu: { icon: 'today', label: 'Today' } as RouteMenuItem,
      middleware: [auth, paid]
    }
  },
  /**
   * 行事曆
   */
  {
    path: '/calendar',
    name: RouteEnum.Calendar,
    component: () => import('@/views/CalendarView.vue'),
    meta: {
      menu: { icon: 'calendar2', label: '行事曆' } as RouteMenuItem,
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 行事曆 - 日記
     */
    path: '/diaryView/:id',
    name: RouteEnum.DiaryView,
    component: () => import('@/views/diary/index.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 行事曆 - 分享日記連結用
     */
    path: '/diaryOpenLink/:openId',
    name: RouteEnum.DiaryOpenLink,
    component: () => import('@/views/diary/index.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 行事曆 - 建立、編輯日記
     */
    path: '/diaryEdit/:id?',
    name: RouteEnum.DiaryEdit,
    component: () => import('@/views/diary/DiaryEditView.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 上課回顧
     */
    path: '/videoLibrary',
    name: RouteEnum.VideoLibrary,
    component: () => import('@/views/videoLibrary/index.vue'),
    meta: {
      menu: { icon: 'videoReview', label: '上課回顧' } as RouteMenuItem,
      middleware: [auth, paid]
    }
  },
  {
    /**
     * AI 助教
     */
    path: '/aiTutor',
    name: RouteEnum.AiTutor,
    component: () => import('@/views/AiTutorView.vue'),
    meta: {
      menu: { icon: 'ai', label: '助教' } as RouteMenuItem,
      middleware: [webview, auth, paid]
    }
  },
  {
    /**
     * 學習指標
     */
    path: '/learningStatistics',
    name: RouteEnum.LearningStatistics,
    component: () => import('@/views/learningStatistics/index.vue'),
    meta: {
      menu: { icon: 'chart', label: '學習指標' } as RouteMenuItem,
      middleware: [lineBinding, auth, paid]
    }
  },
  {
    /**
     * 學習進度
     */
    path: '/learningProgress',
    name: RouteEnum.LearningProgress,
    component: () => import('@/views/learningProgress/index.vue'),
    meta: {
      menu: { icon: 'cup', label: '學習進度' } as RouteMenuItem,
      middleware: [lineBinding, auth, paid]
    }
  },
  {
    /**
     * TOEIC Test
     */
    path: '/toeic',
    name: RouteEnum.Toeic,
    component: () => import('@/views/toeic/index.vue'),
    meta: {
      menu: { icon: 'ETS', label: 'TOEIC' } as RouteMenuItem,
      middleware: [auth, paid]
    }
  },
  {
    /**
     * TOEIC Certificate
     */
    path: '/toeicCertificate',
    name: RouteEnum.ToeicCertificate,
    component: () => import('@/views/toeic/ToeicCertificate.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * TOEIC Exam
     */
    path: '/toeicExam',
    name: RouteEnum.ToeicExam,
    component: () => import('@/views/toeic/ToeicExamPage.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * TOEIC Record
     */
    path: '/toeicRecord',
    name: RouteEnum.ToeicRecord,
    component: () => import('@/views/toeic/ToeicRecordPage.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 舊版家庭作業
     */
    path: '/homework',
    name: RouteEnum.Homework,
    component: () => import('@/views/oldHomework/HomeworkView.vue'),
    meta: {
      menu: { icon: 'briefcase', label: '家庭作業' } as RouteMenuItem,
      middleware: [webview, auth, paid]
    }
  },
  {
    /**
     * 新版家庭作業 (2026/01/26 上線)
     */
    path: '/new-homework',
    name: RouteEnum.NewHomework,
    redirect: { name: RouteEnum.NewHomeworkEntry },
    component: RouterView,
    meta: {
      menu: { icon: 'briefcase', label: '家庭作業' } as RouteMenuItem,
      middleware: [auth, paid]
    },
    children: [
      {
        /**
         * 新版家庭作業 - 入口頁
         */
        path: '',
        name: RouteEnum.NewHomeworkEntry,
        component: () => import('@/views/homeworkLatest/index.vue'),
        meta: {
          middleware: [auth, paid]
        }
      },
      {
        /**
         * 新版家庭作業 - 寫作
         */
        path: 'writing',
        name: RouteEnum.NewHomeworkWriting,
        component: () => import('@/views/homeworkLatest/WritingWorkView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        }
      },
      {
        /**
         * 新版家庭作業 - 寫作記錄
         */
        path: 'writingLog',
        name: RouteEnum.NewHomeworkWritingLog,
        component: () => import('@/views/homeworkLatest/WritingWorkView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        }
      },
      {
        /**
         * 新版家庭作業 - 朗讀
         */
        path: 'readAloud',
        name: RouteEnum.NewHomeworkReadWork,
        component: () => import('@/views/homeworkLatest/ReadAloudWorkView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        }
      },
      {
        /**
         * 新版家庭作業 - 情境對話主題列表
         */
        path: 'conversation',
        name: RouteEnum.NewHomeworkConversation,
        component: () => import('@/views/homeworkLatest/ConversationListView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        }
      },
      {
        /**
         * 新版家庭作業 - 情境對話作答
         */
        path: 'conversationWork/:conversationId([A-Za-z0-9_-]{2,})',
        name: RouteEnum.NewHomeworkConversationWork,
        component: () => import('@/views/homeworkLatest/ConversationWorkView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        },
        beforeEnter: (to: RouteLocationNormalized) => {
          const rawParam: string = to.params.conversationId as string;

          try {
            const num: number = base64UrlToNumber(rawParam);
            if (!Number.isInteger(num)) throw new Error(`Invalid conversationId: ${rawParam}`);
          } catch {
            return { name: RouteEnum.Index };
          }
        }
      },
      {
        /**
         * 新版家庭作業 - 情境對話紀錄
         */
        path: 'conversationLog',
        name: RouteEnum.NewHomeworkConversationLog,
        component: () => import('@/views/homeworkLatest/ConversationWorkView.vue'),
        meta: {
          middleware: [auth, paid, appSchema]
        }
      }
    ]
  },
  {
    /**
     * FunTV
     */
    path: '/funTv',
    name: RouteEnum.FunTv,
    component: () => import('@/views/FunTvView.vue'),
    meta: {
      middleware: [webview, auth, paid],
      layout: 'NoLayout'
    }
  },
  {
    /**
     * 學習紀錄
     */
    path: '/readingRecord',
    name: RouteEnum.ReadingRecord,
    component: () => import('@/views/reading/ReadingRecordView.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 上課紀錄
     */
    path: '/courseRecord',
    name: RouteEnum.CourseRecord,
    component: () => import('@/views/courseRecord/index.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  // 學習診療室 & 學習相談室已經不開課
  // {
  //   /**
  //    * 一對一課程
  //    * @remarks 用 Intranet 內部系統開的課程
  //    */
  //   path: '/consulting',
  //   name: RouteEnum.Consulting,
  //   component: () => import('@/views/ConsultingView.vue'),
  //   meta: {
  //     middleware: [auth]
  //   }
  // },
  {
    /**
     * CEFR 測驗
     */
    path: '/cefrTest',
    name: RouteEnum.CefrTest,
    component: () => import('@/views/CefrTest.vue'),
    meta: {
      middleware: [auth, paid]
    }
  },
  {
    /**
     * 影片播放
     */
    path: '/course-video',
    name: RouteEnum.CourseVideo,
    component: () => import('@/views/CourseVideoView.vue'),
    meta: {
      layout: 'NoLayout',
      allowAllVersions: true,
      middleware: [webview, auth, paid]
    }
  },
  {
    /**
     * 書店
     */
    path: '/bookstore',
    name: RouteEnum.BookStore,
    component: () => import('@/views/bookStore/index.vue'),
    meta: {
      layout: 'DefaultLayout',
      middleware: [webview, auth, paid]
    }
  },
  {
    /**
     * 舊 Fun Coin 路徑重定向至書店
     */
    path: '/funCoin',
    redirect: '/bookstore'
  },
  {
    /**
     * 客製化首頁
     */
    path: '/custom-home-page',
    name: RouteEnum.CustomHomePage,
    component: () => import('@/views/CustomHomeView.vue'),
    meta: {
      layout: 'NoLayout',
      middleware: [auth, paid]
    }
  }
];
